import React, { useState, ChangeEvent, FormEvent, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import BackgroundPattern from '../components/BackgroundPattern';
import LocationPicker from '../components/LocationPicker';
import Footer from '../components/Footer';
import ServiceAreaPicker, { ServiceArea } from '../components/ServiceAreaPicker';

const libraries: ("places")[] = ["places"];

interface Experience {
  years: number;
  role: string;
  place: string;
}

interface FormData {
  userType: 'customer' | 'caregiver';
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  latitude: string;
  longitude: string;
  serviceAreas: ServiceArea[];
  yearsOfExperience: string;
  experiences: Experience[];
  servicesOffered: string[];
  hourlyRateMin: string;
  hourlyRateMax: string;
  profilePhoto: File | null;
  nidFrontSide: File | null;
  nidBackSide: File | null;
  // Demographics
  dateOfBirth: string;
  gender: string;
  weight: string;
  // Medical Profile
  chronicConditions: string[];
  otherConditions: string;
  medications: string;
  bloodGroup: string;
  allergies: string;
  dietaryRestrictions: string;
  mobilityLevel: string;
  // Emergency Contact
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
  emergencyContactAltPhone: string;
  // Preferred Hospital
  hospitalName: string;
  hospitalAddress: string;
  hospitalPhone: string;
}

const Register: React.FC = () => {
  usePageTitle('Register');
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  
  const { isLoaded: isMapsLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: libraries
  });

  // Get initial user type from navigation state if available
  const initialUserType = (location.state as { userType?: 'customer' | 'caregiver' })?.userType || 'customer';

  const [step, setStep] = useState<number>(1);
  const [hospitalAutocomplete, setHospitalAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [previewUrls, setPreviewUrls] = useState<{
    profilePhoto?: string;
    nidFrontSide?: string;
    nidBackSide?: string;
  }>({});
  const [formData, setFormData] = useState<FormData>({
    userType: initialUserType,
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '+880',
    // Location fields
    address: '',
    city: '',
    state: '',
    zipCode: '',
    latitude: '',
    longitude: '',
    serviceAreas: [],
    // Caregiver specific
    yearsOfExperience: '',
    experiences: [],
    servicesOffered: [],
    hourlyRateMin: '',
    hourlyRateMax: '',
    // File uploads
    profilePhoto: null,
    nidFrontSide: null,
    nidBackSide: null,
    // Demographics
    dateOfBirth: '',
    gender: '',
    weight: '',
    // Medical Profile
    chronicConditions: [],
    otherConditions: '',
    medications: '',
    bloodGroup: '',
    allergies: '',
    dietaryRestrictions: '',
    mobilityLevel: 'independent',
    // Emergency Contact
    emergencyContactName: '',
    emergencyContactPhone: '+880',
    emergencyContactRelationship: '',
    emergencyContactAltPhone: '+880',
    // Preferred Hospital
    hospitalName: '',
    hospitalAddress: '',
    hospitalPhone: ''
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  
  // Temporary state for adding experience
  const [currentExp, setCurrentExp] = useState<Experience>({ years: 0, role: '', place: '' });

  const addExperience = () => {
    if (currentExp.years > 0 && currentExp.role && currentExp.place) {
      setFormData(prev => ({
        ...prev,
        experiences: [...prev.experiences, currentExp]
      }));
      setCurrentExp({ years: 0, role: '', place: '' });
    }
  };

  const removeExperience = (index: number) => {
    setFormData(prev => ({
      ...prev,
      experiences: prev.experiences.filter((_, i) => i !== index)
    }));
  };

  const handleServiceAreasChange = useCallback((areas: ServiceArea[]) => {
    setFormData(prev => {
      // Prevent update if areas are identical to avoid loops (though useCallback helps, deep check is safer if ServiceAreaPicker is trigger-happy)
      if (prev.serviceAreas === areas) return prev;
      
      const update = { ...prev, serviceAreas: areas };
      // Sync primary location for backend compatibility
      if (areas.length > 0) {
          update.latitude = areas[0].lat.toString();
          update.longitude = areas[0].lng.toString();
          update.address = areas[0].address;
          // Heuristic: Use name as city if it looks like one, or just store name
          update.city = areas[0].name; 
      }
      return update;
    });
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;
    const checked = target.checked;
    const files = target.files;

    if (['phone', 'emergencyContactPhone', 'emergencyContactAltPhone'].includes(name)) {
      if (!value.startsWith('+880')) return;
      if (value.length > 14) return;
      if (!/^\d*$/.test(value.slice(4))) return;
      setFormData({ ...formData, [name]: value });
      return;
    }
    
    if (type === 'file' && files && files[0]) {
      const file = files[0];
      
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError(`File ${file.name} is too large. Maximum size is 5MB.`);
        // Reset the file input
        target.value = '';
        return;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setPreviewUrls(prev => ({
        ...prev,
        [name]: previewUrl
      }));
      setError(''); // Clear error if successful

      setFormData({ ...formData, [name]: files[0] });
    } else if (type === 'checkbox') {
      // Handle servicesOffered checkboxes
      if (name === 'servicesOffered') {
        const services = [...formData.servicesOffered];
        if (checked) {
          services.push(value);
        } else {
          const index = services.indexOf(value);
          if (index > -1) services.splice(index, 1);
        }
        setFormData({ ...formData, servicesOffered: services });
      }
      // Handle chronicConditions checkboxes
      else if (name === 'chronicConditions') {
        const conditions = [...formData.chronicConditions];
        if (checked) {
          conditions.push(value);
        } else {
          const index = conditions.indexOf(value);
          if (index > -1) conditions.splice(index, 1);
        }
        setFormData({ ...formData, chronicConditions: conditions });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const nextStep = () => {
    if (step === 1) {
      if (!formData.email || !formData.password || !formData.confirmPassword) {
        setError('Please fill in all required fields');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Please enter a valid email address');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
    }
    if (step === 2) {
      if (!formData.firstName || !formData.lastName || !formData.phone) {
        setError('Please fill in all required fields');
        return;
      }
      if (!formData.phone.startsWith('+880') || formData.phone.length !== 14) {
        setError('Phone number must start with +880 and be 14 digits total');
        return;
      }
      if (!formData.latitude || !formData.longitude) {
        setError('Please select your location on the map');
        return;
      }
      if (!formData.profilePhoto) {
        setError('Profile photo is required');
        return;
      }
      if (formData.userType === 'caregiver' && !formData.nidFrontSide) {
        setError('National ID is required for caregivers');
        return;
      }
    }
    if (step === 3 && formData.userType === 'customer') {
      if (!formData.dateOfBirth || !formData.gender || !formData.weight) {
        setError('Please provide your date of birth, gender, and weight');
        return;
      }
    }
    if (step === 4 && formData.userType === 'customer') {
      if (!formData.bloodGroup) {
        setError('Please select your blood group');
        return;
      }
      if (!formData.mobilityLevel) {
        setError('Please select your mobility level');
        return;
      }
    }
    if (step === 5 && formData.userType === 'customer') {
      if (!formData.emergencyContactName || !formData.emergencyContactPhone || !formData.emergencyContactRelationship) {
        setError('Please provide emergency contact information');
        return;
      }
      if (formData.emergencyContactPhone.length !== 14) {
        setError('Emergency contact phone number must be 14 digits including +880');
        return;
      }
      if (formData.emergencyContactAltPhone !== '+880' && formData.emergencyContactAltPhone.length !== 14) {
        setError('Alternate emergency phone number must be 14 digits including +880');
        return;
      }
    }
    setError('');
    
    // Prevent going past max steps
    const maxStep = formData.userType === 'customer' ? 5 : 3;
    if (step < maxStep) {
      setStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setError('');
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const onHospitalLoad = (autocomplete: google.maps.places.Autocomplete) => {
    setHospitalAutocomplete(autocomplete);
  };

  const onHospitalPlaceChanged = () => {
    if (hospitalAutocomplete) {
      const place = hospitalAutocomplete.getPlace();
      setFormData(prev => ({
        ...prev,
        hospitalName: place.name || prev.hospitalName,
        hospitalAddress: place.formatted_address || prev.hospitalAddress,
        hospitalPhone: place.formatted_phone_number || prev.hospitalPhone
      }));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate caregiver requirements
    if (formData.userType === 'caregiver') {
      if (formData.experiences.length === 0) {
        setError('Please add at least one professional experience record');
        setLoading(false);
        return;
      }
      if (formData.servicesOffered.length === 0) {
        setError('Please select at least one service you offer');
        setLoading(false);
        return;
      }
    }

    // Validate file uploads
    if (!formData.profilePhoto) {
      setError('Profile photo is required');
      setLoading(false);
      return;
    }
    if (formData.userType === 'caregiver' && !formData.nidFrontSide) {
      setError('National ID (front side) is required for caregivers');
      setLoading(false);
      return;
    }

    // Create FormData for file upload
    const submitData = new FormData();
    submitData.append('email', formData.email);
    submitData.append('password', formData.password);
    submitData.append('userType', formData.userType);
    submitData.append('firstName', formData.firstName);
    submitData.append('lastName', formData.lastName);
    submitData.append('phone', formData.phone);
    submitData.append('profilePhoto', formData.profilePhoto);
    
    if (formData.nidFrontSide) {
      submitData.append('nidFrontSide', formData.nidFrontSide);
    }
    if (formData.nidBackSide) {
      submitData.append('nidBackSide', formData.nidBackSide);
    }

    // Prepare location data
    const locationData = {
      coordinates: [
        parseFloat(formData.longitude) || 0,
        parseFloat(formData.latitude) || 0
      ],
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode
    };

    if (formData.userType === 'caregiver') {
      submitData.append('areaOfService', JSON.stringify(locationData));
      
      if (formData.serviceAreas && formData.serviceAreas.length > 0) {
        const areas = formData.serviceAreas.map(area => {
             // Convert bounds to polygon if available, else default box around point
             let polygonCoords: number[][];
             if (area.bounds) {
                 const { north, south, east, west } = area.bounds;
                 polygonCoords = [
                     [west, south], // SW
                     [east, south], // SE
                     [east, north], // NE
                     [west, north], // NW
                     [west, south]  // Close loop
                 ];
             } else {
                 // Fallback 5km box/ approx 0.045 deg
                 const delta = 0.045;
                 polygonCoords = [
                     [area.lng - delta, area.lat - delta],
                     [area.lng + delta, area.lat - delta],
                     [area.lng + delta, area.lat + delta],
                     [area.lng - delta, area.lat + delta],
                     [area.lng - delta, area.lat - delta]
                 ];
             }

             return {
                name: area.name,
                address: area.address,
                location: {
                    type: 'Polygon',
                    coordinates: [polygonCoords]
                }
             };
        });
        submitData.append('serviceAreas', JSON.stringify(areas));
      }

      submitData.append('servicesOffered', JSON.stringify(formData.servicesOffered));
      
      // Calculate total years for backward compatibility and add structured experiences
      if (formData.experiences.length > 0) {
        submitData.append('experiences', JSON.stringify(formData.experiences));
        const totalYears = formData.experiences.reduce((acc, curr) => acc + curr.years, 0);
        submitData.append('yearsOfExperience', totalYears.toString());
      } else {
        submitData.append('yearsOfExperience', '0');
      }
    } else {
      submitData.append('location', JSON.stringify(locationData));
      
      // Add demographics
      if (formData.dateOfBirth) submitData.append('dateOfBirth', formData.dateOfBirth);
      if (formData.gender) submitData.append('gender', formData.gender);
      if (formData.weight) submitData.append('weight', formData.weight);

      // Add medical profile
      const medicalProfile: any = {};
      if (formData.chronicConditions.length > 0) {
        medicalProfile.chronicConditions = formData.chronicConditions;
      }
      if (formData.otherConditions) {
        medicalProfile.otherConditions = formData.otherConditions;
      }
      if (formData.medications) {
        // Parse medications into structured format
        medicalProfile.currentMedications = formData.medications.split('\n')
          .filter(m => m.trim())
          .map(med => ({ name: med.trim() }));
      }
      if (formData.bloodGroup) {
        medicalProfile.bloodGroup = formData.bloodGroup;
      }
      if (formData.allergies) {
        medicalProfile.allergies = formData.allergies;
      }
      if (formData.dietaryRestrictions) {
        medicalProfile.dietaryRestrictions = formData.dietaryRestrictions;
      }
      if (formData.mobilityLevel) {
        medicalProfile.mobilityLevel = formData.mobilityLevel;
      }
      
      if (Object.keys(medicalProfile).length > 0) {
        submitData.append('medicalProfile', JSON.stringify(medicalProfile));
      }

      // Add emergency contact
      if (formData.emergencyContactName) {
        const emergencyContact: any = {
          name: formData.emergencyContactName,
          phone: formData.emergencyContactPhone,
          relationship: formData.emergencyContactRelationship
        };
        if (formData.emergencyContactAltPhone) {
          emergencyContact.alternatePhone = formData.emergencyContactAltPhone;
        }
        submitData.append('emergencyContact', JSON.stringify(emergencyContact));
      }

      // Add preferred hospital
      if (formData.hospitalName || formData.hospitalAddress || formData.hospitalPhone) {
        const preferredHospital: any = {};
        if (formData.hospitalName) preferredHospital.name = formData.hospitalName;
        if (formData.hospitalAddress) preferredHospital.address = formData.hospitalAddress;
        if (formData.hospitalPhone) preferredHospital.phone = formData.hospitalPhone;
        submitData.append('preferredHospital', JSON.stringify(preferredHospital));
      }
    }

    const result = await register(submitData);
    
    if (result.success && result.user) {
      navigate(`/${result.user.userType}/dashboard`);
    } else {
      setError(result.message || 'Registration failed');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col relative">
      <BackgroundPattern />
      {/* Top Header */}
      <header className="bg-primary-600 shadow-lg relative z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl sm:text-3xl font-bold font-proyas tracking-wider text-white hover:text-gray-100 transition-colors">PROYAS</Link>
            <Link
              to="/"
              className="px-3 py-2 sm:px-4 sm:py-2 bg-white text-brand rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm sm:text-base"
              style={{ color: '#7f0a0f' }}
            >
              Home
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-grow py-8 px-4 relative z-10">
        
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-xl overflow-hidden animate-fade-in">
          {/* Form Header */}
          <div className="bg-primary-600 text-white p-6">
            <h2 className="text-2xl font-bold">Join <span className="font-proyas tracking-wider">PROYAS</span></h2>
            <p className="text-primary-100 text-sm mt-1">
              {formData.userType === 'customer' ? 'Find quality care' : 'Provide quality care'}
            </p>
          </div>

        {/* Progress Bar */}
        <div className="h-2 bg-gray-300">
          <div 
            className="h-full bg-gradient-to-r from-primary-600 to-primary-700 transition-all duration-300"
            style={{ width: `${(step / (formData.userType === 'customer' ? 5 : 3)) * 100}%` }}
          />
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Step 1: User Type & Account */}
          {step === 1 && (
            <div className="space-y-6">
              
              {/* User Type Selection */}
              <div>
                 <h3 className="text-lg font-bold text-gray-900 text-center mb-4">Choose Your Role</h3>
                 <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, userType: 'customer' })}
                    className={`relative p-4 rounded-xl border-2 transition-all flex flex-col items-center text-center gap-2 group ${
                      formData.userType === 'customer'
                        ? 'border-primary-500 bg-primary-50 shadow-md ring-2 ring-primary-200 ring-offset-1'
                        : 'border-gray-200 bg-white hover:border-primary-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`p-3 rounded-full ${formData.userType === 'customer' ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-500 group-hover:bg-primary-50 group-hover:text-primary-500'}`}>
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    </div>
                    <div>
                      <h4 className={`font-bold ${formData.userType === 'customer' ? 'text-primary-800' : 'text-gray-700'}`}>Care Seeker</h4>
                      <p className="text-xs text-gray-500 mt-1">Found quality caregivers near me</p>
                    </div>
                    {formData.userType === 'customer' && (
                       <div className="absolute top-2 right-2 text-primary-500"><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg></div>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, userType: 'caregiver' })}
                    className={`relative p-4 rounded-xl border-2 transition-all flex flex-col items-center text-center gap-2 group ${
                      formData.userType === 'caregiver'
                        ? 'border-primary-500 bg-primary-50 shadow-md ring-2 ring-primary-200 ring-offset-1'
                        : 'border-gray-200 bg-white hover:border-primary-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`p-3 rounded-full ${formData.userType === 'caregiver' ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-500 group-hover:bg-primary-50 group-hover:text-primary-500'}`}>
                       <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                    </div>
                    <div>
                      <h4 className={`font-bold ${formData.userType === 'caregiver' ? 'text-primary-800' : 'text-gray-700'}`}>Caregiver</h4>
                      <p className="text-xs text-gray-500 mt-1">Provide service and earn</p>
                    </div>
                    {formData.userType === 'caregiver' && (
                       <div className="absolute top-2 right-2 text-primary-500"><svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg></div>
                    )}
                  </button>
                 </div>
              </div>

              <div className="space-y-4">
                 <div className="relative">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block pl-1">Email Address</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" /></svg>
                      </div>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-shadow"
                        placeholder="name@example.com"
                      />
                    </div>
                 </div>

                 <div className="relative">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block pl-1">Password</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                      </div>
                      <input
                        type="password"
                        name="password"
                        required
                        autoComplete="new-password"
                        value={formData.password}
                        onChange={handleChange}
                         className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-shadow"
                        placeholder="••••••••"
                      />
                    </div>
                 </div>

                 <div className="relative">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1 block pl-1">Confirm Password</label>
                     <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      </div>
                      <input
                        type="password"
                        name="confirmPassword"
                        required
                        autoComplete="new-password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                         className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-shadow"
                        placeholder="••••••••"
                      />
                    </div>
                 </div>
              </div>
            </div>
          )}

          {/* Step 2: Personal Information */}
          {step === 2 && (
            <div className="space-y-6 animate-fade-in">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4 shadow-inner">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Personal Details</h3>
                <p className="text-gray-500 mt-2">Let's get to know you better</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 block">First Name</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-50 transition-all outline-none bg-gray-50 focus:bg-white"
                      placeholder="John"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 block">Last Name</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                       <svg className="h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-50 transition-all outline-none bg-gray-50 focus:bg-white"
                      placeholder="Doe"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 block">
                  Phone Number
                  <span className="text-red-500"> *</span>
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-50 transition-all outline-none bg-gray-50 focus:bg-white"
                    placeholder="+880 1XXX-XXXXXX"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700 block mb-1">
                  {formData.userType === 'customer' ? 'Home Location' : 'Service Area'}
                  <span className="text-red-500"> *</span>
                </label>
                <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm relative">
                {isMapsLoaded ? (
                  formData.userType === 'caregiver' ? (
                    <ServiceAreaPicker 
                      selectedAreas={formData.serviceAreas}
                      onAreasChange={handleServiceAreasChange}
                    />
                  ) : (
                  <LocationPicker
                    isPrecise={formData.userType === 'customer'}
                    onLocationSelect={(location) => {
                      setFormData({
                        ...formData,
                        latitude: location.lat.toString(),
                        longitude: location.lng.toString(),
                        address: location.address,
                        city: location.city,
                        state: location.state,
                        zipCode: location.zipCode
                      });
                    }}
                    initialLocation={
                      formData.latitude && formData.longitude
                        ? {
                            lat: parseFloat(formData.latitude),
                            lng: parseFloat(formData.longitude),
                            address: formData.address
                          }
                        : undefined
                    }
                  />
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 bg-gray-50">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-3"></div>
                    <span className="text-gray-500 font-medium">Loading maps...</span>
                  </div>
                )}
                </div>
              </div>

              {/* File Uploads */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 block">
                    Profile Photo <span className="text-red-500">*</span>
                  </label>
                  
                  <div className="relative group">
                    <input
                      type="file"
                      name="profilePhoto"
                      id="profilePhoto"
                      required
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleChange}
                      className="hidden"
                    />
                    <label 
                      htmlFor="profilePhoto"
                      className={`
                        flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-all
                        ${previewUrls.profilePhoto 
                          ? 'border-primary-500 bg-primary-50' 
                          : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-primary-400'
                        }
                      `}
                    >
                      {previewUrls.profilePhoto ? (
                        <div className="flex items-center space-x-4">
                          <img 
                            src={previewUrls.profilePhoto} 
                            alt="Preview" 
                            className="h-24 w-24 object-cover rounded-full border-4 border-white shadow-md"
                          />
                          <div className="text-left">
                            <p className="text-sm font-medium text-primary-700">Photo Selected</p>
                            <p className="text-xs text-gray-500 mt-1 max-w-[150px] truncate">{formData.profilePhoto?.name}</p>
                            <span className="text-xs text-primary-600 hover:underline mt-2 inline-block">Click to change</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center pt-5 pb-6">
                           <svg className="w-8 h-8 text-gray-400 mb-2 group-hover:text-primary-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="mb-2 text-sm text-gray-500"><span className="font-semibold text-primary-600">Click to upload</span> profile photo</p>
                          <p className="text-xs text-gray-500">PNG, JPG (MAX 5MB)</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

              {formData.userType === 'caregiver' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 block">
                      NID Front <span className="text-red-500">*</span>
                    </label>
                     <div className="relative group">
                        <input
                          type="file"
                          name="nidFrontSide"
                          id="nidFrontSide"
                          required
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={handleChange}
                          className="hidden"
                        />
                         <label 
                          htmlFor="nidFrontSide"
                          className={`
                            flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition-all overflow-hidden relative
                            ${previewUrls.nidFrontSide
                              ? 'border-primary-500 bg-gray-900' 
                              : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-primary-400'
                            }
                          `}
                        >
                          {previewUrls.nidFrontSide ? (
                             <>
                              <img 
                                src={previewUrls.nidFrontSide} 
                                alt="NID Front" 
                                className="absolute inset-0 w-full h-full object-contain opacity-80"
                              />
                               <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 hover:bg-opacity-30 transition-all">
                                <span className="text-white text-xs font-medium bg-black bg-opacity-50 px-3 py-1 rounded-full">Change Front</span>
                              </div>
                             </>
                          ) : (
                            <div className="flex flex-col items-center p-4 text-center">
                              <svg className="w-8 h-8 text-gray-400 mb-2 group-hover:text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"></path></svg>
                              <p className="text-sm font-medium text-gray-600">Front Side</p>
                              <p className="text-xs text-gray-400 mt-1">Required for verification</p>
                            </div>
                          )}
                        </label>
                     </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 block">
                      NID Back <span className="font-normal text-gray-400">(Optional)</span>
                    </label>
                     <div className="relative group">
                        <input
                          type="file"
                          name="nidBackSide"
                          id="nidBackSide"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={handleChange}
                          className="hidden"
                        />
                         <label 
                          htmlFor="nidBackSide"
                          className={`
                            flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition-all overflow-hidden relative
                            ${previewUrls.nidBackSide
                              ? 'border-primary-500 bg-gray-900' 
                              : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-primary-400'
                            }
                          `}
                        >
                          {previewUrls.nidBackSide ? (
                             <>
                              <img 
                                src={previewUrls.nidBackSide} 
                                alt="NID Back" 
                                className="absolute inset-0 w-full h-full object-contain opacity-80"
                              />
                               <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 hover:bg-opacity-30 transition-all">
                                <span className="text-white text-xs font-medium bg-black bg-opacity-50 px-3 py-1 rounded-full">Change Back</span>
                              </div>
                             </>
                          ) : (
                            <div className="flex flex-col items-center p-4 text-center">
                              <svg className="w-8 h-8 text-gray-400 mb-2 group-hover:text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path></svg>
                              <p className="text-sm font-medium text-gray-600">Back Side</p>
                              <p className="text-xs text-gray-400 mt-1">Upload if available</p>
                            </div>
                          )}
                        </label>
                     </div>
                  </div>
                </div>
              )}
              </div>
            </div>
          )}

          {/* Step 3: Role-Specific Information */}
          {step === 3 && formData.userType === 'caregiver' && (
            <div className="space-y-8 animate-fade-in">
              <div className="text-center mb-6">
                <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4 shadow-inner">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Professional Profile</h3>
                <p className="text-gray-500 mt-2">Tell us about your experience and skills</p>
              </div>
              
              {/* Professional Experience Section */}
              <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-primary-500"></div>
                
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Work History</h3>
                    <p className="text-sm text-gray-500">Add your nursing or caregiving experience</p>
                  </div>
                  <span className="text-xs font-semibold text-primary-700 bg-primary-50 px-3 py-1 rounded-full border border-primary-100">
                    {formData.experiences.length} Added
                  </span>
                </div>
                
                {/* List of Added Experiences */}
                {formData.experiences.length > 0 && (
                  <div className="mb-6 space-y-3">
                    {formData.experiences.map((exp, index) => (
                      <div key={index} className="group flex flex-wrap justify-between items-center bg-gray-50 p-4 rounded-xl border border-gray-100 hover:border-primary-200 hover:shadow-sm transition-all gap-2">
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-gray-200 text-gray-400 flex-shrink-0">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-gray-800 break-words">{exp.role}</p>
                            <div className="flex flex-wrap items-center text-sm text-gray-600 mt-0.5">
                              <span className="font-medium text-primary-600 whitespace-nowrap">{exp.years} {exp.years === 1 ? 'Year' : 'Years'}</span>
                              <span className="mx-2 text-gray-300">•</span>
                              <span className="break-words">{exp.place}</span>
                            </div>
                          </div>
                        </div>
                        <button 
                          type="button"
                          onClick={() => removeExperience(index)}
                          className="text-gray-400 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-colors flex-shrink-0"
                          title="Remove Experience"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add New Experience Inputs */}
                <div className="bg-gray-50/50 p-4 rounded-xl border border-dashed border-gray-300">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Add New Record</h4>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-5">
                      <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Role/Title</label>
                      <input
                        type="text"
                        placeholder="e.g. Senior Nurse"
                        value={currentExp.role}
                        onChange={(e) => setCurrentExp({...currentExp, role: e.target.value})}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-shadow"
                      />
                    </div>
                    <div className="md:col-span-4">
                      <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Institution</label>
                      <input
                        type="text"
                        placeholder="e.g. Dhaka Medical"
                        value={currentExp.place}
                        onChange={(e) => setCurrentExp({...currentExp, place: e.target.value})}
                        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-shadow"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Duration (Yrs)</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="0"
                          placeholder="0"
                          value={currentExp.years || ''}
                          onChange={(e) => setCurrentExp({...currentExp, years: parseInt(e.target.value) || 0})}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-shadow"
                        />
                        <button
                          type="button"
                          onClick={addExperience}
                          disabled={!currentExp.role || !currentExp.place || currentExp.years <= 0}
                          className="px-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <label className="block text-sm font-bold text-gray-800 mb-3">
                  Services You Offer <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { 
                      id: 'nurse-care', 
                      title: 'Nurse Care', 
                      desc: 'Medical assistance, medication, health monitoring', 
                      icon: (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path></svg>
                      )
                    },
                    { 
                      id: 'everyday-assistance', 
                      title: 'Everyday Assistance', 
                      desc: 'Mobility support, hygiene, companionship', 
                      icon: (
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                      )
                    }
                  ].map(service => (
                    <label 
                      key={service.id} 
                      className={`
                        flex items-start space-x-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 group
                        ${formData.servicesOffered.includes(service.id) 
                          ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500 shadow-sm' 
                          : 'border-gray-200 bg-white hover:border-primary-200 hover:bg-gray-50'
                        }
                      `}
                    >
                      <input
                        type="checkbox"
                        name="servicesOffered"
                        value={service.id}
                        checked={formData.servicesOffered.includes(service.id)}
                        onChange={handleChange}
                        className="hidden"
                      />
                      <div className={`p-2 rounded-lg ${formData.servicesOffered.includes(service.id) ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-500 group-hover:bg-primary-50 group-hover:text-primary-600'}`}>
                        {service.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className={`font-bold ${formData.servicesOffered.includes(service.id) ? 'text-primary-900' : 'text-gray-800'}`}>{service.title}</span>
                          {formData.servicesOffered.includes(service.id) && (
                            <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">{service.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Demographics (Customer only) */}
          {step === 3 && formData.userType === 'customer' && (
            <div className="space-y-8 animate-fade-in">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4 shadow-inner">
                  <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">About You</h3>
                <p className="text-gray-500 mt-2">Personal details to help us serve you better</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 block">
                    Date of Birth <span className="text-red-500">*</span>
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      type="date"
                      name="dateOfBirth"
                      required
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-50 transition-all outline-none bg-gray-50 focus:bg-white"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                   <label className="text-sm font-semibold text-gray-700 block">
                    Weight (Optional)
                  </label>
                  <div className="relative group">
                     <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                      </svg>
                    </div>
                    <input
                      type="number"
                      name="weight"
                      min="0"
                      value={formData.weight}
                      onChange={handleChange}
                      className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-50 transition-all outline-none bg-gray-50 focus:bg-white"
                      placeholder="70"
                    />
                    <span className="absolute right-4 top-3.5 text-gray-500 font-medium">kg</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-sm font-semibold text-gray-700 block">
                  Gender <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { id: 'male', label: 'Male' },
                    { id: 'female', label: 'Female' },
                    { id: 'other', label: 'Other' }
                  ].map((option) => (
                     <button
                      key={option.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, gender: option.id as any })}
                      className={`
                        relative group flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all duration-200
                        ${formData.gender === option.id
                          ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-sm ring-1 ring-primary-500'
                          : 'border-gray-100 bg-white text-gray-500 hover:border-primary-200 hover:bg-gray-50'
                        }
                      `}
                    >
                      <span className="font-medium text-lg">{option.label}</span>
                      {formData.gender === option.id && (
                        <div className="absolute top-2 right-2 text-primary-500 animate-scale-in">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Medical Profile (Customer only) */}
          {step === 4 && formData.userType === 'customer' && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900">Medical Information</h3>
                <p className="text-sm text-gray-500 mt-1">Help us match you with the right caregiver</p>
              </div>

              {/* Blood Group - High Priority visual */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Blood Group <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                  {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                    <button
                      type="button"
                      key={bg}
                      onClick={() => setFormData({ ...formData, bloodGroup: bg })}
                      className={`
                        py-2 px-1 rounded-md text-sm font-bold border transition-all
                        ${formData.bloodGroup === bg
                          ? 'bg-red-50 text-red-600 border-red-200 ring-2 ring-red-500 ring-offset-1'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-red-200 hover:bg-red-50'
                        }
                      `}
                    >
                      {bg}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chronic Conditions - Chip/Tag selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chronic Conditions
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {['diabetes', 'hypertension', 'heart-disease', 'dementia', 'arthritis', 'asthma', 'parkinsons', 'stroke-history', 'kidney-disease', 'cancer', 'copd', 'osteoporosis', 'other'].map(condition => (
                    <label 
                      key={condition} 
                      className={`
                        flex items-center p-3 rounded-lg border cursor-pointer transition-all
                        ${formData.chronicConditions.includes(condition) 
                          ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-sm' 
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                        }
                      `}
                    >
                      <input
                        type="checkbox"
                        name="chronicConditions"
                        value={condition}
                        checked={formData.chronicConditions.includes(condition)}
                        onChange={handleChange}
                        className="hidden" 
                      />
                      <div className="flex-1 text-sm font-medium text-center">
                        {condition.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                      {formData.chronicConditions.includes(condition) && (
                        <span className="text-primary-500 ml-2">✓</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {formData.chronicConditions.includes('other') && (
                <div className="animate-fade-in">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specify Condition
                  </label>
                  <input
                    type="text"
                    name="otherConditions"
                    value={formData.otherConditions}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g. Thyroid, Migraine..."
                    autoFocus
                  />
                </div>
              )}

              {/* Mobility - Cards */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobility Level <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { val: 'independent', label: 'Independent', desc: 'Can move freely' },
                    { val: 'needs-assistance', label: 'Needs Support', desc: 'Needs walking aid/support' },
                    { val: 'wheelchair', label: 'Wheelchair', desc: 'Uses wheelchair' },
                    { val: 'bedridden', label: 'Bedridden', desc: 'Confined to bed' }
                  ].map(option => (
                    <button
                      key={option.val}
                      type="button"
                      onClick={() => setFormData({ ...formData, mobilityLevel: option.val })}
                      className={`
                        p-3 rounded-lg border-2 flex flex-col items-center justify-center text-center transition-all
                        ${formData.mobilityLevel === option.val
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 bg-white hover:border-primary-200'
                        }
                      `}
                    >
                      <div className={`font-semibold text-sm ${formData.mobilityLevel === option.val ? 'text-primary-700' : 'text-gray-900'}`}>
                        {option.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{option.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Text Areas Section - Side by side on larger screens */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Medications
                  </label>
                  <textarea
                    name="medications"
                    value={formData.medications}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder="List key medications..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Allergies (Food/Drug)
                  </label>
                  <textarea
                    name="allergies"
                    value={formData.allergies}
                    onChange={handleChange}
                    rows={2}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                    placeholder="Any known allergies..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Emergency Contact & Preferred Hospital (Customer only) */}
          {step === 5 && formData.userType === 'customer' && (
            <div className="space-y-8">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Emergency Protocol</h3>
                <p className="text-sm text-gray-500 mt-1">Ensuring your safety is our top priority</p>
              </div>

              {/* Emergency Contact Card */}
              <div className="bg-red-50 rounded-xl p-5 border border-red-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-white rounded-full shadow-sm text-red-500">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h4 className="font-semibold text-gray-900">Primary Contact</h4>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="emergencyContactName"
                      required
                      value={formData.emergencyContactName}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-white border border-red-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-gray-900 placeholder-gray-400"
                      placeholder="e.g. Spouse, Child name"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="emergencyContactPhone"
                        required
                        value={formData.emergencyContactPhone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-white border border-red-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-gray-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                        Relation
                      </label>
                      <input
                        type="text"
                        name="emergencyContactRelationship"
                        required
                        value={formData.emergencyContactRelationship}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-white border border-red-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-gray-900 placeholder-gray-400"
                        placeholder="e.g. Son"
                      />
                    </div>
                  </div>

                  <div>
                     <label className="block text-xs font-bold text-gray-400 uppercase tracking-wide mb-1">
                      Alternate Phone (Backup)
                    </label>
                    <input
                      type="tel"
                      name="emergencyContactAltPhone"
                      value={formData.emergencyContactAltPhone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 text-gray-900"
                    />
                  </div>
                </div>
              </div>

              {/* Preferred Hospital Card */}
              <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-white rounded-full shadow-sm text-blue-500">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Trusted Hospital</h4>
                    <p className="text-xs text-blue-600">Where should we go in an emergency?</p>
                  </div>
                </div>

                {isMapsLoaded && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">
                        Search Hospital
                      </label>
                      <Autocomplete
                        onLoad={onHospitalLoad}
                        onPlaceChanged={onHospitalPlaceChanged}
                        options={{
                          types: ['hospital', 'health'],
                          componentRestrictions: { country: 'bd' },
                          fields: ['name', 'formatted_address', 'formatted_phone_number']
                        }}
                      >
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Type hospital name..."
                            className="w-full pl-10 pr-4 py-2 bg-white border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 transition-shadow shadow-sm hover:shadow-md"
                          />
                          <div className="absolute left-3 top-2.5 text-gray-400">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                        </div>
                      </Autocomplete>
                    </div>

                    {/* Auto-filled details */}
                    {(formData.hospitalName || formData.hospitalAddress) && (
                      <div className="bg-white bg-opacity-60 p-3 rounded-lg border border-blue-100 animate-fade-in">
                        {formData.hospitalName && (
                          <div className="font-medium text-gray-900">{formData.hospitalName}</div>
                        )}
                        {formData.hospitalAddress && (
                          <div className="text-xs text-gray-500 mt-1">{formData.hospitalAddress}</div>
                        )}
                        {formData.hospitalPhone && (
                          <div className="text-xs text-gray-500 mt-1">{formData.hospitalPhone}</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 pt-4">
            {step > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            )}
            
            {step < (formData.userType === 'customer' ? 5 : 3) ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium hover:opacity-90 transition-colors"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Creating Account...' : 'Complete Registration'}
              </button>
            )}
          </div>

          <div className="text-center pt-4">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-primary-600 hover:text-brand">
                Log in
              </Link>
            </p>
          </div>
        </form>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default Register;
